import { useTheme } from "@emotion/react";
import React, { useEffect, useRef } from "react";

type Props = {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  className?: string;
  options: string[];
  isDisabled?: boolean;
  autoFocus?: boolean;
  error?: string | null;
};

export const SelectField: React.FunctionComponent<Props> = ({
  value,
  onChange,
  label,
  className,
  options,
  isDisabled = false,
  autoFocus = false,
  error,
}) => {
  const theme = useTheme();
  const selectRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (autoFocus && selectRef.current) {
      selectRef.current.focus();
    }
  }, [autoFocus]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  return (
    <div css={{ display: "flex", flexDirection: "column", width: "100%" }} className={className}>
      {label && (
        <label
          css={{
            color: theme.colors.primary,
            fontSize: "0.875rem",
            fontFamily: theme.displayFontFamily,
            marginBottom: "0.25rem",
          }}
        >
          {label}
        </label>
      )}
      <select
        ref={selectRef}
        value={value}
        onChange={handleChange}
        css={{
          width: "100%",
          padding: "0.5rem 0.75rem",
          fontSize: "1rem",
          borderRadius: "6px",
          backgroundColor: theme.colors.primaryLight,
          border: "none",
          ":focus": {
            outline: `1px solid ${error ? theme.colors.danger : theme.colors.primary}`,
          },
          cursor: isDisabled ? "not-allowed" : "pointer",
        }}
        disabled={isDisabled}
      >
        {options.map((option, index) => (
          <option key={index} value={option}>
            {option}
          </option>
        ))}
      </select>
      {error && <span css={{ fontSize: "0.75rem", color: theme.colors.danger, marginTop: "0.125rem" }}>{error}</span>}
    </div>
  );
};
